/**
 * Cleans phone number by removing non-digit characters and handling the India-specific
 * country code prefix (91) that sometimes appears twice.
 */
export function cleanPhoneNumber(
  phone: string | null | undefined,
  countryCode: string | null | undefined
): string {
  if (!phone) return "";

  let digits = phone.replace(/\D/g, "");

  // Handle India-specific case: remove 91 prefix if it makes number 12 digits
  if (digits.length === 12 && digits.startsWith("91")) {
    digits = digits.slice(2);
  }

  return digits;
}

/**
 * Formats a phone number for display by prefixing the country code.
 */
export function formatPhoneNumber(
  phone: string | null | undefined,
  countryCode: string | null | undefined
): string {
  if (!phone) return "";
  const code = countryCode || "";
  return `${code} ${phone}`.trim();
}

/**
 * Validates a phone number by ensuring its cleaned length is between 10 and 15 digits.
 */
export function isValidPhoneNumber(phone: string | null | undefined): boolean {
  if (!phone) return false;
  const cleaned = phone.replace(/\D/g, "");
  return cleaned.length >= 10 && cleaned.length <= 15;
}

/**
 * Returns true if the phone number is non-empty and consists only of zeros (e.g. 0000000000).
 */
export const isInvalidAllZerosPhone = (
  phone: string | null | undefined
): boolean => {
  if (!phone || !String(phone).trim()) return false;
  const digits = String(phone).replace(/\D/g, "");
  return digits.length > 0 && /^0+$/.test(digits);
};

/**
 * Validates a phone number against country-specific rules and returns an error message
 * string when invalid, or undefined when valid.
 *
 * Rules:
 *  - India (+91): exactly 10 digits required.
 *  - All other codes: 6–14 digits accepted (covers the shortest valid national numbers
 *    through the longest ITU-T E.164 subscriber numbers).
 *  - Empty / null / undefined values are considered valid (use required-field checks
 *    separately).
 */
export const validatePhoneForCountry = (
  phone: string | null | undefined,
  countryCode: string | null | undefined
): string | undefined => {
  if (!phone) return undefined;
  const digits = phone.replace(/\D/g, "");
  if (countryCode === "+91") {
    return digits.length === 10
      ? undefined
      : "Phone number must be exactly 10 digits";
  }
  return digits.length >= 6 && digits.length <= 14
    ? undefined
    : "Phone number must be between 6 and 14 digits";
};

/**
 * Strips all non-digit characters from a phone input value and caps it at maxLength digits.
 * Use this for controlled phone input onChange handlers to enforce digit-only entry.
 * Default maxLength of 15 follows the ITU-T E.164 maximum subscriber number length.
 */
export const maskPhoneInput = (
  value: string,
  maxLength: number = 15
): string => value.replace(/\D/g, "").slice(0, maxLength);

/**
 * Strips the country code prefix from a phone number if it already includes it.
 * For example, "+917498829782" with country code "+91" becomes "7498829782".
 */
export function stripCountryCodeFromPhone(
  phone: string | null | undefined,
  countryCode: string | null | undefined
): string {
  if (!phone || !countryCode) return phone || "";
  
  // Remove any non-digit characters from country code for comparison
  const countryCodeDigits = countryCode.replace(/\D/g, "");
  
  // Remove all non-digit characters from phone for processing
  let phoneDigits = phone.replace(/\D/g, "");
  
  // Check if phone number starts with country code digits
  if (phoneDigits.startsWith(countryCodeDigits) && countryCodeDigits.length > 0) {
    // Remove the country code prefix
    phoneDigits = phoneDigits.slice(countryCodeDigits.length);
  }
  
  return phoneDigits;
}
